import {useContext, useState} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {Box, Stack, Typography, IconButton, Tooltip, Divider} from "@mui/material";
import {
    IconDatabase,
    IconPlus,
    IconSettings,
    IconLayoutDashboard,
    IconCircleFilled,
    IconChevronRight,
} from "@tabler/icons-react";
import {Context} from "../../../../context/AppContext";
import {CreateTopicModal} from "./CreateTopicModal";

const NavButton = ({icon, label, active, onClick}) => (
    <Stack
        direction={"row"} alignItems={"center"} gap={1.5}
        onClick={onClick}
        sx={{
            px: 1.5, py: 1, borderRadius: 1.5, cursor: "pointer",
            fontSize: 13,
            color: active ? "text.primary" : "text.secondary",
            bgcolor: active ? "action.selected" : "transparent",
            "&:hover": {bgcolor: active ? "action.selected" : "action.hover"},
        }}
    >
        {icon}
        <Typography fontSize={13} color={"inherit"}>{label}</Typography>
    </Stack>
);

export const KnowledgeManagerLayout = ({children,project}) => {
    const {projectId, topicId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {state,getAllKbTopics} = useContext(Context);
    const {kbTopics} = state;
    const organization = project?.settings?.organization;

    const base = `/project/${projectId}/knowledge-manager`;
    const isDashboard = location.pathname === base;
    const isSettings = location.pathname === `${base}/general-settings`;
    const isTopicActive = (id) => location.pathname === `${base}/${id}`;
    const [newTopicModalOpen, setNewTopicModalOpen] = useState(false);

    const totalDocs = kbTopics.reduce((acc, t) => acc + (t.documentCount || 0), 0);

    return (
        <Stack direction={"row"} sx={{height: "100%", minHeight: 0}}>

            <Stack
                sx={{
                    width: 256, flexShrink: 0, height: "100%",
                    borderRight: "1px solid", borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >

                <Box sx={{px: 2, py: 2, borderBottom: "1px solid", borderColor: "divider"}}>
                    <Typography fontWeight={700} fontSize={15}>Knowledge Base</Typography>
                    <Typography fontSize={11} color={"text.secondary"}>Knowledge Manager</Typography>
                </Box>


                <Stack sx={{px: 1.5, pt: 1.5, pb: 0.5}} gap={0.5}>
                    <NavButton
                        icon={<IconLayoutDashboard size={16}/>}
                        label={"Dashboard"}
                        active={isDashboard}
                        onClick={() => navigate(base)}
                    />
                    <NavButton
                        icon={<IconSettings size={16}/>}
                        label={"Global Settings"}
                        active={isSettings}
                        onClick={() => navigate(`${base}/general-settings`)}
                    />
                </Stack>

                <Divider sx={{my: 1}}/>

                <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{px: 2, py: 1}}>
                    <Typography fontSize={11} color={"text.secondary"}
                                sx={{textTransform: "uppercase", letterSpacing: 0.5}}>
                        Topics
                    </Typography>
                    <Tooltip title={"New Topic"}>
                        <IconButton onClick={() => setNewTopicModalOpen(true)} size={"small"}>
                            <IconPlus size={14}/>
                        </IconButton>
                    </Tooltip>
                </Stack>


                <Stack sx={{flex: 1, px: 1.5, overflowY: "auto"}} gap={0.25}>
                    {kbTopics.map(topic => {
                        const active = isTopicActive(topic.id);
                        return (
                            <Stack
                                key={topic.id}
                                direction={"row"} alignItems={"center"} gap={1.5}
                                onClick={() => navigate(`${base}/${topic.id}`)}
                                sx={{
                                    px: 1.5, py: 1, borderRadius: 1.5, cursor: "pointer",
                                    color: active ? "text.primary" : "text.secondary",
                                    bgcolor: active ? "action.selected" : "transparent",
                                    "&:hover": {bgcolor: active ? "action.selected" : "action.hover"},
                                }}
                            >
                                <IconCircleFilled size={10} color={topic.color}/>
                                <Typography fontSize={13} color={"inherit"} noWrap sx={{flex: 1}}>
                                    {topic.name}
                                </Typography>
                                {topic.server && (
                                    <Tooltip title={`Server: ${topic.server.status}`}>
                                        <Box sx={{
                                            width: 6, height: 6, borderRadius: "50%",
                                            bgcolor: topic.server.status === "online" ? "success.main"
                                                : topic.server.status === "connecting" ? "warning.main"
                                                    : "error.light",
                                        }}/>
                                    </Tooltip>
                                )}
                                <IconChevronRight size={12} style={{opacity: active ? 0.5 : 0}}/>
                            </Stack>
                        );
                    })}
                </Stack>


                <Box sx={{borderTop: "1px solid", borderColor: "divider", p: 1.5}}>
                    <Stack direction={"row"} alignItems={"center"} gap={1}
                           sx={{fontSize: 11, color: "text.secondary", px: 1}}>
                        <IconDatabase size={12}/>
                        <Typography fontSize={11} color={"text.secondary"}>{kbTopics.length} Topics</Typography>
                        <Typography fontSize={11} color={"text.secondary"}>|</Typography>
                        <Typography fontSize={11} color={"text.secondary"}>{totalDocs} Docs</Typography>
                    </Stack>
                </Box>
            </Stack>


            <Box sx={{flex: 1, minWidth: 0, height: "100%", overflow: "auto"}}>
                {children}
            </Box>
            <CreateTopicModal
                open={newTopicModalOpen}
                onClose={() => setNewTopicModalOpen(false)}
                onCreated={() => {
                    getAllKbTopics(organization);
                }}
                project={project}
            />
        </Stack>
    );
};